/**
 * Open the production report in an isolated document before invoking print.
 * This avoids Vuetify/global layout and print rules from splitting the report
 * at the wrong places (the regular page remains unchanged).
 */
const PRINT_CSS = String.raw`
@page { size: A4 landscape; margin: 7mm 8mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; color: #19232b; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; line-height: 1.24; }
.production-print-sheet { display: block; width: 100%; }
.print-product-block { width: 100%; max-width: 100%; }
.print-product-block + .print-product-block { break-before: page; }
.print-heading { text-align: center; margin: 0 0 5px; }
.print-heading h1 { font-size: 15px; line-height: 1.15; font-weight: 700; margin: 0 0 2px; }
.print-heading h2 { font-size: 11px; line-height: 1.15; text-decoration: underline; letter-spacing: .5px; margin: 0 0 2px; }
.print-doc-code { font-size: 9px; }
.print-doc-code span { padding: 0 5px; }
.print-meta { width: 100%; border-collapse: collapse; margin-bottom: 5px; table-layout: fixed; }
.print-meta td { border: 1px solid #cbd2d8; padding: 4px 5px; background: white; vertical-align: top; overflow-wrap: anywhere; }
.print-meta span { display: block; font-size: 8px; color: #4f5963; margin-bottom: 1px; }
.print-meta strong { display: block; font-size: 9px; font-weight: 700; }
.print-product-title { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding: 5px 6px; background: #eaf0f3; border-top: 1px solid #7f8f99; border-bottom: 1px solid #7f8f99; margin: 5px 0; }
.print-product-title strong { font-size: 11px; }
.print-product-title span { font-size: 9px; overflow-wrap: anywhere; }
.print-meta--batch { margin-bottom: 5px; }
.print-section-heading { font-size: 9.5px; font-weight: 700; background: #e8edf0; border-left: 3px solid #5b7485; padding: 4px 6px; margin: 6px 0 2px; break-after: avoid; page-break-after: avoid; }
.print-table { width: 100%; border-collapse: collapse; table-layout: auto; font-size: 8.5px; }
.print-table th, .print-table td { border: 1px solid #bac5cd; padding: 3px 5px; line-height: 1.2; vertical-align: middle; overflow-wrap: anywhere; }
.print-table th { text-align: left; background: #ebf0f2; font-weight: 700; }
.print-table thead { display: table-header-group; }
.print-table tr { break-inside: avoid; page-break-inside: avoid; }
.print-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.print-total-row td { font-weight: 700; background: #f2f5f6; }
.print-trace-table th:first-child { width: 18%; }
.print-trace-table th:nth-child(3) { width: 17%; }
.print-cost-table th, .print-cost-table td { text-align: center; }
.print-remarks { padding: 4px 6px; border: 1px solid #d5dce1; margin-top: 6px; }
.print-signatures { display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px; margin-top: 23px; text-align: center; break-inside: avoid; }
.print-signatures > div { border-top: 1px solid #7d8b95; padding-top: 4px; font-size: 9px; }
.print-footnote { text-align: center; padding-top: 6px; font-size: 7px; color: #67717b; }
@media screen {
  body { background: #e6ebef; padding: 14px; }
  .production-print-sheet { width: 281mm; min-height: 194mm; background: white; padding: 7mm 8mm; margin: 0 auto; box-shadow: 0 3px 15px rgba(0,0,0,.15); }
}
@media print {
  html, body { background: white !important; width: auto !important; height: auto !important; }
  .production-print-sheet { padding: 0 !important; width: 100% !important; min-height: 0 !important; margin: 0 !important; box-shadow: none !important; }
  .print-product-block { margin: 0 !important; padding: 0 !important; }
  .print-product-block + .print-product-block { break-before: page; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char]));
}

export function openProductionPrintPreview(reportHtml, documentCode) {
  // Open synchronously on the user's click so popup blockers don't interfere.
  const preview = window.open('', '_blank', 'width=1150,height=860');
  if (!preview) return false;

  const doc = `<!doctype html><html lang="en"><head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(documentCode)} — Production Report</title>
<style>${PRINT_CSS}</style>
</head><body><div class="production-print-sheet">${reportHtml}</div>
<script>window.addEventListener('load', function () {
  setTimeout(function () { window.focus(); window.print(); }, 180);
});<\/script></body></html>`;
  preview.document.open();
  preview.document.write(doc);
  preview.document.close();
  return true;
}
